/**
 * Shared prompt section for @ context selection.
 * Appended to every app's system prompt so the LLM understands
 * the `@[DisplayName](metadata)` mention syntax.
 */
export const contextSelectionPrompt = `

## Context Selection

Users can attach context to their messages using the \`@\` mention syntax: \`@[DisplayName]({{metadata_json}})\`. Each context has a \`"type"\` field indicating its category. Understand each context type and its intent:

### Files (\`"type":"file"\`)
The user is referencing a specific uploaded file by name. Intent: the user wants you to read, analyze, or use content from that file. Use \`DocIntelligence\` to process the referenced file.
- Metadata fields: \`filename\`

### Knowledge Bases (\`"type":"knowledge_base"\`)
The user wants to search a specific internal knowledge base. Intent: retrieve information from the named knowledge base using \`KnowledgeBaseTool\`.
- Metadata fields: \`agent_name\`, \`agent_description\`
- Extract the knowledge base name from the \`agent_name\` field and pass it to \`KnowledgeBaseTool\` as \`knowledge_base_to_search\`.

### Managers (\`"type":"manager"\`)
The user is referencing a specific investment manager or product. Intent: the user wants data or analysis related to this manager. Use \`ManagerData\` when the query is about quantitative manager data (performance, holdings, ratings). Use \`KnowledgeBaseTool\` when a knowledge base is also selected — the manager context narrows which manager the knowledge base search should focus on.
- Metadata fields: \`productId\`, \`productShortName\`

### People (\`"type":"person"\`)
The user is referencing a specific person at Cambridge Associates. Intent: the query involves or is about this person (e.g., sending an email, looking up their work, or filtering results by author).
- Metadata fields: \`email\`, \`display_name\`

### Handling Multiple Contexts
When a message contains multiple context annotations, respect each one independently and determine the correct tool(s) based on their combination:
- **Knowledge Base + Manager**: Search the specified knowledge base filtered by or focused on the referenced manager. Call \`KnowledgeBaseTool\` with the knowledge base name and include the manager/product details in the question. Do **not** route to \`ManagerData\` unless the user is explicitly asking for quantitative manager data.
- **Knowledge Base + File**: Search the knowledge base and also consider the file content as additional context.
- **Multiple Knowledge Bases**: Make separate \`KnowledgeBaseTool\` calls for each knowledge base.
- Always prioritize the explicitly selected context types over implicit routing based on keywords in the query.`;
